import { ChangeEvent, forwardRef, useCallback, useState } from 'react'
import { PatternFormat } from 'react-number-format'

import { EmailSuggestions } from '@/components/emailSuggestions'

import { EmailAutocompleteProps, TCustomInputType, TIndicatorType } from './types'

const Indicator = ({ isFocus, isError, isValid }: TIndicatorType) => {
  return (
    <div className="indicator-container">
      <div
        className={
          'indicator' +
          (isFocus ? ' focus' : '') +
          (isError ? ' error' : '') +
          (isValid ? ' valid' : '')
        }
      />
    </div>
  )
}

// eslint-disable-next-line react/display-name
export const CustomInput = forwardRef<HTMLInputElement, TCustomInputType>(
  (
    {
      format,
      defaultValue,
      value,
      isError,
      isFocus,
      isValid,
      iconNode,
      showInputIndicator = true,
      name,
      type,
      autoComplete,
      ...props
    },
    ref
  ) => {
    let iconContainer
    if (iconNode) {
      iconContainer = <div className="icon-container">{iconNode}</div>
    }

    const inputNode = format ? (
      <PatternFormat format={format} getInputRef={ref} {...props} />
    ) : (
      <input
        ref={ref}
        name={name}
        type={type}
        autoComplete={autoComplete}
        defaultValue={defaultValue}
        value={value}
        {...props}
      />
    )
    return (
      <div className="input-with-indicator-container">
        {iconContainer}
        {showInputIndicator && <Indicator isFocus={isFocus} isError={isError} isValid={isValid} />}
        {inputNode}
      </div>
    )
  }
)

const emailDomains: string[] = [
  'gmail.com',
  'yahoo.com',
  'outlook.com',
  'hotmail.com',
  'icloud.com',
]

export const EmailAutocompleteInput = ({
  value,
  onChange,
  isError,
  isFocus,
  isValid,
  readOnly,
  disabled,
  onFocus,
  onBlur,
  showInputIndicator = true,
  iconNode,
  inputPlaceholder,
}: EmailAutocompleteProps) => {
  const [suggestions, setSuggestions] = useState<string[]>([])

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value
      onChange(newValue)
      if (newValue.length < 3 || !newValue.includes('@')) {
        setSuggestions([])
        return
      }

      const [localPart, domainPart] = newValue.split('@')
      if (domainPart) {
        const filteredDomains = emailDomains.filter((domain) => domain.startsWith(domainPart))
        setSuggestions(filteredDomains.map((domain) => `${localPart}@${domain}`))
      } else {
        setSuggestions(emailDomains.map((domain) => `${localPart}@${domain}`))
      }
    },
    [onChange]
  )

  const handleSelect = useCallback(
    (suggestion: string) => {
      onChange(suggestion)
      setSuggestions([])
    },
    [onChange]
  )

  return (
    <div className="email-input-container">
      <CustomInput
        name="email"
        type="email"
        autoComplete="email"
        value={value}
        isError={isError}
        isFocus={isFocus}
        isValid={isValid}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        readOnly={readOnly}
        disabled={disabled}
        placeholder={inputPlaceholder}
        iconNode={iconNode}
        showInputIndicator={showInputIndicator}
      />
      <EmailSuggestions suggestions={suggestions} onSelect={handleSelect} />
    </div>
  )
}
